import { useNavigate } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { ButtonField } from '@wk/components-react16';
import { IconAlertTriangle } from '@tabler/icons-react';

import {
  InteractionStatus,
  type PublicClientApplication,
} from '@azure/msal-browser';
import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';

import { CustomNavigationClient } from '@/lib/NavigationClient';
import { hasAuthTokens } from '@/lib/authToken';
import { loginRequest } from '@/config/msalConfig';
import { reactPlugin } from '@/config/telemetry';
import {
  clearRedirectAuthError,
  getRedirectAuthError,
  setRedirectAuthError,
  shouldStartInteractiveLogin,
  subscribeToRedirectAuthError,
} from './authRedirectState';

interface AppProvidersProps {
  msalInstance: PublicClientApplication;
  children: ReactNode;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

function InteractiveAuthGate({ children }: { children: ReactNode }) {
  const { accounts, inProgress, instance } = useMsal();
  const loginStarted = useRef(false);
  const redirectError = useSyncExternalStore(
    subscribeToRedirectAuthError,
    getRedirectAuthError,
    getRedirectAuthError,
  );

  useEffect(() => {
    if (!shouldStartInteractiveLogin({
      accountCount: accounts.length,
      isInteractionIdle: inProgress === InteractionStatus.None,
      loginStarted: loginStarted.current,
      redirectError,
    })) {
      return;
    }

    loginStarted.current = true;
    void instance.loginRedirect(loginRequest).catch((error: unknown) => {
      loginStarted.current = false;
      setRedirectAuthError(error);
    });
  }, [accounts.length, inProgress, instance, redirectError]);

  if (redirectError) {
    const retryLogin = () => {
      if (loginStarted.current) {
        return;
      }

      loginStarted.current = true;
      clearRedirectAuthError();
      void instance.loginRedirect(loginRequest).catch((error: unknown) => {
        loginStarted.current = false;
        setRedirectAuthError(error);
      });
    };

    return (
      <main className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
        <section
          role="alert"
          className="w-full max-w-lg border border-gray-300 bg-white p-6 shadow-sm"
        >
          <div className="mb-4 flex items-center gap-3">
            <IconAlertTriangle
              aria-hidden="true"
              className="shrink-0 text-red-700"
              size={28}
            />
            <h1 className="text-xl font-bold text-gray-900">
              Unable to sign in
            </h1>
          </div>
          <p className="mb-5 text-sm text-gray-700">
            We could not complete authentication. Try signing in again. If the
            problem continues, contact support.
          </p>
          <ButtonField>
            <button type="button" onClick={retryLogin}>
              Try again
            </button>
          </ButtonField>
        </section>
      </main>
    );
  }

  if (inProgress !== InteractionStatus.None || accounts.length === 0) {
    return null;
  }

  return children;
}

function AppProviders({ msalInstance, children }: AppProvidersProps) {
  const navigate = useNavigate();
  const navigationClient = new CustomNavigationClient(navigate);
  msalInstance.setNavigationClient(navigationClient);

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          {hasAuthTokens() ? (
            // auth_token mode (E2E): tokens are injected via window.__ORR_TOKENS__
            // and the bootstrap faked the active account, so the interactive
            // login gate must not run — it would redirect to Entra.
            children
          ) : (
            <InteractiveAuthGate>{children}</InteractiveAuthGate>
          )}
        </QueryClientProvider>
      </MsalProvider>
    </AppInsightsContext.Provider>
  );
}

export default AppProviders;
